/**
 * PromotionViewer — the left-pane media viewer of the promotion detail page.
 *
 * Three modes, toggled by a segmented control:
 *   - Pages  (BookOpen):        single-image pager with prev/next arrows,
 *                               a "2 / 14" counter and arrow-key navigation.
 *   - Grid   (LayoutGrid):      thumbnail grid; clicking a thumbnail jumps to
 *                               that page in Pages mode.
 *   - Scroll (GalleryVertical): vertical stack of all pages.
 *
 * A fullscreen button opens the pager inside a full-size Modal.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  GalleryVertical,
  LayoutGrid,
  Maximize2,
  Play,
  Tag,
} from 'lucide-react';
import { Img, Modal } from '@/components/ui';
import { VideoPlayer } from '@/components/ui/VideoPlayer';
import { ImagePresets, isVideoMedia, safeImageUrl } from '@/utils/safeImageUrl';
import type { Media } from '@/types';

export interface PromotionViewerProps {
  media: Media[];
  /** Promotion name — used for alt text and the fullscreen modal title. */
  name: string;
}

type ViewerMode = 'pages' | 'grid' | 'scroll';

interface ViewerPage {
  key: string;
  media: Media;
  /** Set for video pages: the HLS/mp4 URL to play. */
  videoUrl?: string | null;
  /** Still image for a video page (its VIDEO_THUMBNAIL), used as poster + grid tile. */
  poster?: Media;
}

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const MODES: Array<{ key: ViewerMode; label: string; icon: typeof BookOpen }> = [
  { key: 'pages', label: 'Pages', icon: BookOpen },
  { key: 'grid', label: 'Grid', icon: LayoutGrid },
  { key: 'scroll', label: 'Scroll', icon: GalleryVertical },
];

const COVER_TYPES = new Set(['THUMBNAIL', 'VIDEO_THUMBNAIL']);

/**
 * `sizes` for a page in the viewer column. PromotionDetailPage lays it out as
 * `lg:grid-cols-[1fr_420px]` with a 2rem gap inside the `max-w-7xl px-4`
 * column: 1248 − 420 − 32 = 796 px at full width, the viewport minus
 * 32 + 420 + 32 px from `lg`, and the whole column below that.
 */
const VIEWER_SIZES =
  '(min-width: 1280px) 796px, (min-width: 1024px) calc(100vw - 484px), calc(100vw - 32px)';

/**
 * Flatten Media[] into one viewer page per URL.
 * Skips failed media and THUMBNAIL / VIDEO_THUMBNAIL entries (card artwork,
 * not a flyer page) — a VIDEO_THUMBNAIL becomes the poster of the video page.
 */
function buildPages(media: Media[]): ViewerPage[] {
  const videoPoster = media.find((m) => m.mediaType === 'VIDEO_THUMBNAIL');
  return media
    .filter((m) => m.status !== 'failed' && !COVER_TYPES.has(m.mediaType ?? ''))
    .flatMap((m) => {
      const urls = Array.isArray(m.url) ? m.url : [];
      const video = isVideoMedia(m);
      return urls.map((url, i) => {
        // Single-URL clone so <Img> (which renders media.url[0]) shows THIS page.
        // The row's width/height/blurhash describe its FIRST file only (the
        // backend copies them from item 0 of a multi-upload), so later pages
        // must not inherit them as their own intrinsic size.
        const single: Media =
          i === 0
            ? { ...m, url: [url] }
            : { ...m, url: [url], width: undefined, height: undefined, blurhash: undefined };
        return video
          ? { key: `${m.id}-${i}`, media: single, videoUrl: safeImageUrl(single), poster: videoPoster }
          : { key: `${m.id}-${i}`, media: single };
      });
    });
}

interface PageMediaProps {
  page: ViewerPage;
  alt: string;
  className: string;
  /** `<img sizes>` for the frame this page is shown in. */
  sizes: string;
  /** The page is the promotion page's LCP image (page 1, inline pager). */
  priority?: boolean;
}

/** One page's content at full size: a video player or a contained image. */
function PageMedia({ page, alt, className, sizes, priority = false }: PageMediaProps) {
  if (page.videoUrl) {
    return (
      <VideoPlayer
        src={page.videoUrl}
        // A poster attribute takes one URL, no srcset: a fixed ladder step
        // that suits a phone at 2x and a desktop frame alike.
        poster={safeImageUrl(page.poster, { ...ImagePresets.full, width: 960 })}
        label={alt}
        className={cn('w-full rounded-2xl', className)}
      />
    );
  }
  return (
    <Img
      media={page.media}
      transform={ImagePresets.full}
      sizes={sizes}
      priority={priority}
      fit="contain"
      alt={alt}
      className={cn('w-full rounded-2xl bg-line-light', className)}
    />
  );
}

interface PagerProps {
  pages: ViewerPage[];
  name: string;
  index: number;
  onIndexChange: (index: number) => void;
  /** Sizing for the image frame — the pager needs an explicit height. */
  frameClassName: string;
  /** `<img sizes>` matching the frame's width. */
  sizes: string;
  /** Load page 1 as the page's LCP image (the inline pager, not the modal). */
  priorityFirstPage?: boolean;
}

/** Single-image pager: image + prev/next arrows + page counter. */
function Pager({
  pages,
  name,
  index,
  onIndexChange,
  frameClassName,
  sizes,
  priorityFirstPage = false,
}: PagerProps) {
  const page = pages[Math.min(index, pages.length - 1)];
  if (!page) return null;

  const canPrev = index > 0;
  const canNext = index < pages.length - 1;

  const arrowClasses =
    'absolute top-1/2 -translate-y-1/2 rounded-full bg-surface/90 p-2 text-ink shadow-md backdrop-blur transition-all duration-200 hover:bg-surface hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="relative h-full">
      <PageMedia
        page={page}
        alt={`${name} — page ${index + 1} of ${pages.length}`}
        className={frameClassName}
        sizes={sizes}
        priority={priorityFirstPage && index === 0}
      />
      {pages.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous page"
            disabled={!canPrev}
            onClick={() => onIndexChange(Math.max(0, index - 1))}
            className={cn(arrowClasses, 'left-3')}
          >
            <ChevronLeft size={22} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Next page"
            disabled={!canNext}
            onClick={() => onIndexChange(Math.min(pages.length - 1, index + 1))}
            className={cn(arrowClasses, 'right-3')}
          >
            <ChevronRight size={22} aria-hidden="true" />
          </button>
        </>
      )}
      <span
        aria-live="polite"
        className={cn(
          'absolute left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white',
          // Keep clear of the native video control bar.
          page.videoUrl ? 'top-3' : 'bottom-3',
        )}
      >
        {index + 1} / {pages.length}
      </span>
    </div>
  );
}

export function PromotionViewer({ media, name }: PromotionViewerProps) {
  const [mode, setMode] = useState<ViewerMode>('pages');
  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const pages = useMemo(() => buildPages(media), [media]);

  // Keep the index in range if the media list changes underneath us.
  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(0, pages.length - 1)));
  }, [pages.length]);

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(pages.length - 1, i + 1)),
    [pages.length],
  );

  // Arrow-key navigation while the pager is visible (inline or fullscreen).
  useEffect(() => {
    if (pages.length <= 1) return;
    if (mode !== 'pages' && !fullscreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, fullscreen, pages.length, goPrev, goNext]);

  if (pages.length === 0) {
    return (
      <div className="flex h-[420px] flex-col items-center justify-center gap-3 rounded-2xl bg-line-light md:h-[520px]">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface shadow-sm">
          <Tag size={28} className="text-ink-tertiary" aria-hidden="true" />
        </span>
        <p className="text-sm font-medium text-ink-secondary">No media available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Mode toggle + fullscreen */}
      <div className="flex items-center justify-between gap-3">
        <div
          role="group"
          aria-label="Viewer mode"
          className="inline-flex items-center gap-1 rounded-xl border border-line bg-surface p-1"
        >
          {MODES.map(({ key, label, icon: Icon }) => {
            const active = mode === key;
            // Named by aria-label: below `sm` the text label is display:none,
            // which would leave three unnamed icon buttons (Lighthouse
            // button-name). Same words as the visible label from `sm` up.
            return (
              <button
                key={key}
                type="button"
                aria-label={label}
                aria-pressed={active}
                onClick={() => setMode(key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all duration-200',
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-ink-secondary hover:bg-line-light hover:text-ink',
                )}
              >
                <Icon size={15} aria-hidden="true" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          aria-label="View fullscreen"
          title="Fullscreen"
          onClick={() => setFullscreen(true)}
          className="rounded-xl border border-line bg-surface p-2 text-ink-secondary transition-all duration-200 hover:bg-line-light hover:text-ink"
        >
          <Maximize2 size={18} aria-hidden="true" />
        </button>
      </div>

      {/* Pages mode */}
      {mode === 'pages' && (
        <Pager
          pages={pages}
          name={name}
          index={index}
          onIndexChange={setIndex}
          frameClassName="h-[60vh] max-h-[70vh] md:h-[70vh]"
          sizes={VIEWER_SIZES}
          priorityFirstPage
        />
      )}

      {/* Every page as a thumbnail, so a multi-page flyer is in the DOM in full
          (the pager above only renders the current page) — for readers
          skimming it and for crawlers, which never click "next". Lazy images,
          so pages below the fold cost nothing until scrolled to. */}
      {mode === 'pages' && pages.length > 1 && (
        <ol aria-label="All pages" className="flex gap-2 overflow-x-auto pb-1">
          {pages.map((page, i) => (
            <li key={page.key} className="shrink-0">
              <button
                type="button"
                aria-label={`Show page ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => setIndex(i)}
                className={cn(
                  'relative block overflow-hidden rounded-lg border-2 transition-all duration-200',
                  i === index
                    ? 'border-primary'
                    : 'border-transparent opacity-75 hover:opacity-100',
                )}
              >
                <Img
                  media={page.videoUrl ? page.poster : page.media}
                  transform={ImagePresets.thumb}
                  alt={`${name} — page ${i + 1} of ${pages.length}`}
                  className="h-20 w-16"
                />
                {page.videoUrl && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded-full bg-black/60 p-1.5 text-white">
                      <Play size={12} aria-hidden="true" />
                    </span>
                  </span>
                )}
              </button>
            </li>
          ))}
        </ol>
      )}

      {/* Grid mode */}
      {mode === 'grid' && (
        <div className="grid grid-cols-3 gap-2">
          {pages.map((page, i) => (
            <button
              key={page.key}
              type="button"
              aria-label={`Open page ${i + 1}`}
              onClick={() => {
                setIndex(i);
                setMode('pages');
              }}
              className="group relative overflow-hidden rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <Img
                media={page.videoUrl ? page.poster : page.media}
                transform={ImagePresets.card}
                alt={`${name} — page ${i + 1}`}
                className="aspect-[3/4] w-full rounded-xl bg-line-light transition-all duration-200 group-hover:opacity-90"
              />
              {page.videoUrl && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="rounded-full bg-black/60 p-3 text-white">
                    <Play size={20} aria-hidden="true" />
                  </span>
                </span>
              )}
              <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white">
                {i + 1}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Scroll mode */}
      {mode === 'scroll' && (
        <div className="space-y-3">
          {pages.map((page, i) => (
            <div key={page.key} className="relative">
              <PageMedia
                page={page}
                alt={`${name} — page ${i + 1} of ${pages.length}`}
                className="aspect-[3/4]"
                sizes={VIEWER_SIZES}
              />
              <span className="absolute bottom-3 right-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-semibold text-white">
                Page {i + 1}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Fullscreen pager */}
      <Modal open={fullscreen} onClose={() => setFullscreen(false)} title={name} size="full">
        <div className="h-full p-4">
          <Pager
            pages={pages}
            name={name}
            index={index}
            onIndexChange={setIndex}
            frameClassName="h-full"
            sizes="100vw"
          />
        </div>
      </Modal>
    </div>
  );
}

export default PromotionViewer;
